import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  ReferenceLine
} from "recharts";
import './WeeklyChart.css';

const WeeklyChart = ({ data, type = "physical" }) => {
  // Determine colors based on chart type
  const getChartColors = () => {
    if (type === "mental") {
      return {
        gradient1: "#667eea",
        gradient2: "#764ba2",
        stroke: "#667eea",
        fill: "url(#mentalGradient)"
      };
    }
    return {
      gradient1: "#00C523",
      gradient2: "#1d4e5b",
      stroke: "#00C523",
      fill: "url(#physicalGradient)"
    };
  };

  const colors = getChartColors();

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const date = new Date(label);
      const formattedDate = date.toLocaleDateString('sr-RS', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      });

      // Rating description
      const getRatingDescription = (rating) => {
        switch(rating) {
          case 1: return "Veoma loše";
          case 2: return "Loše";
          case 3: return "Osrednje";
          case 4: return "Dobro";
          case 5: return "Odlično";
          default: return "Nema podataka";
        }
      };

      return (
        <div className="custom-tooltip">
          <div className="tooltip-header">
            <div className="tooltip-date">{formattedDate}</div>
            <div className="tooltip-rating">{value}/5</div>
          </div>
          <div className="tooltip-description">
            {getRatingDescription(value)}
          </div>
          <div className="tooltip-arrow"></div>
        </div>
      );
    }
    return null;
  };

  // Custom dot component
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    const rating = payload.rating;

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r="6"
          fill={colors.stroke}
          stroke="rgba(255, 255, 255, 0.8)"
          strokeWidth="2"
          className="chart-dot"
        />
        <circle
          cx={cx}
          cy={cy}
          r="3"
          fill="white"
          className="chart-dot-inner"
        />
      </g>
    );
  };

  // Format date for X-axis
  const formatXAxisDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sr-RS', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Get average rating - only count days with valid ratings (> 0)
  const validRatings = data.filter(item => item.rating && item.rating > 0);
  const averageRating = validRatings.length > 0 
    ? validRatings.reduce((sum, item) => sum + item.rating, 0) / validRatings.length
    : 0;

  return (
    <div className="modern-chart-container">
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <defs>
              <linearGradient id="physicalGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00C523" stopOpacity={0.8}/>
                <stop offset="50%" stopColor="#00C523" stopOpacity={0.4}/>
                <stop offset="100%" stopColor="#1d4e5b" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="mentalGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#667eea" stopOpacity={0.8}/>
                <stop offset="50%" stopColor="#667eea" stopOpacity={0.4}/>
                <stop offset="100%" stopColor="#764ba2" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={colors.gradient1}/>
                <stop offset="100%" stopColor={colors.gradient2}/>
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255, 255, 255, 0.1)"
              vertical={false}
            />

            <XAxis
              dataKey="date"
              tick={{ fill: 'rgba(255, 255, 255, 0.8)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
              tickLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
              tickFormatter={formatXAxisDate}
            />

            <YAxis
              domain={[1, 5]}
              tick={{ fill: 'rgba(255, 255, 255, 0.8)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
              tickLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
              ticks={[1, 2, 3, 4, 5]}
            />

            <Tooltip content={<CustomTooltip />} />

            <ReferenceLine
              y={averageRating}
              stroke="rgba(255, 255, 255, 0.5)"
              strokeDasharray="5 5"
              strokeWidth={2}
            />

            <Area
              type="monotone"
              dataKey="rating"
              stroke="none"
              fill={colors.fill}
              fillOpacity={1}
              animationDuration={1000}
              animationEasing="ease-out"
            />

            <Line
              type="monotone"
              dataKey="rating"
              stroke="url(#lineGradient)"
              strokeWidth={3}
              dot={<CustomDot />}
              activeDot={{
                r: 8,
                fill: colors.stroke,
                stroke: 'white',
                strokeWidth: 2,
                className: 'active-dot'
              }}
              animationDuration={1000}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>


    </div>
  );
};

export default WeeklyChart;
